/**
 * @fileoverview Overlay de Transmisión Broadcast / Pantalla Gigante (/abcdef/overlays/personal-surfer)
 * para la dinámica "Esquivador Personal" (HU-05).
 * 
 * Diseñado para pantallas de estadio Jumbotron y transmisiones OBS/vMix con fondo transparente,
 * renderizado fluido a 60 FPS acelerado por GPU y sincronización en tiempo real vía SignalR.
 * 
 * Regla Zero-Canvas: No renderiza nada en estado idle.
 * Cero exposición de PII sensible (sin números de teléfono en pantalla gigante).
 * 
 * @module components/Overlays/PersonalSurferOverlay
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetPublicConfig, GetLeaderboard } from '../../utilities/DL/PersonalSurfer';
import SignalRService from '../../utilities/WS/SignalRService';
import { PersonalSurfer as PersonalSurferView } from './Overlays/PersonalSurfer';

/**
 * COMPONENTE: PERSONAL SURFER OVERLAY (PARENT CONTROLLER)
 * ------------------------------------------------------------------
 * 1. Lee el roomId desde query params o props.
 * 2. Garantiza fondo transparente total en el DOM (body, html, root).
 * 3. Consulta la configuración inicial pública y el leaderboard vía REST.
 * 4. Establece conexión en tiempo real con SignalR y escucha los eventos de overlay,
 *    configuración, nuevos scores y reinicios.
 * 5. Gestiona el ciclo de vida de animación (flowState: idle -> entering -> visible -> fadingOut -> idle).
 * 6. Pasa el estado al componente visual <PersonalSurferView />.
 * ------------------------------------------------------------------
 */
export function PersonalSurferOverlay({ roomId: propRoomId = '' }) {
    // 1. Obtener roomId de props o query params
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const roomId = useMemo(() => {
        return propRoomId || searchParams.get('roomId') || searchParams.get('room') || '';
    }, [propRoomId, searchParams]);

    // 2. Estados de configuración visual
    const [offsetY, setOffsetY] = useState(0);
    const [banner1536Url, setBanner1536Url] = useState('');
    const [banner1920Url, setBanner1920Url] = useState('');
    const [qrCount, setQrCount] = useState(1);
    const [qrCenterLogoUrl, setQrCenterLogoUrl] = useState('');
    const [overlayMode, setOverlayMode] = useState('invitation'); // 'invitation' | 'leaderboard'
    const [leaderboard, setLeaderboard] = useState([]);
    const [activeTheme, setActiveTheme] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('idle');

    // 3. Control del flujo de visibilidad ('idle' | 'entering' | 'visible' | 'fadingOut')
    const [flowState, setFlowState] = useState('idle');

    // 4. Forzar fondo transparente total para integración OBS / vMix Browser Source
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const rootElement = document.getElementById('root');
        const originalRootBg = rootElement ? rootElement.style.backgroundColor : '';

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            if (rootElement) {
                rootElement.style.backgroundColor = originalRootBg;
            }
        };
    }, []);

    // 5. Control de transiciones de animación (entering -> visible, fadingOut -> idle)
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            timeout = setTimeout(() => setFlowState('visible'), 500);
        } else if (flowState === 'fadingOut') {
            timeout = setTimeout(() => {
                setFlowState('idle');
            }, 500);
        }
        return () => clearTimeout(timeout);
    }, [flowState]);

    const handleOverlayVisibilityChange = useCallback((visible) => {
        if (visible) {
            setFlowState((prev) => {
                if (prev === 'idle' || prev === 'fadingOut') {
                    return 'entering';
                }
                return prev;
            });
        } else {
            setFlowState((prev) => {
                if (prev !== 'idle' && prev !== 'fadingOut') {
                    return 'fadingOut';
                }
                return prev;
            });
        }
    }, []);

    // 6. Aplicar datos de configuración y overlay
    const applyOverlayData = useCallback((data) => {
        if (!data) return;

        if (data.overlayVisible !== undefined) {
            handleOverlayVisibilityChange(Boolean(data.overlayVisible));
        } else if (data.isOverlayVisible !== undefined) {
            handleOverlayVisibilityChange(Boolean(data.isOverlayVisible));
        }

        if (data.overlayMode) {
            setOverlayMode(String(data.overlayMode).toLowerCase());
        }

        if (data.overlayQrCount !== undefined) {
            setQrCount(Number(data.overlayQrCount) || 1);
        } else if (data.qrCount !== undefined) {
            setQrCount(Number(data.qrCount) || 1);
        }

        if (data.overlayOffsetY !== undefined) {
            setOffsetY(Number(data.overlayOffsetY) || 0);
        } else if (data.offsetY !== undefined) {
            setOffsetY(Number(data.offsetY) || 0);
        }

        if (data.overlayBanner1536Url !== undefined) {
            setBanner1536Url(data.overlayBanner1536Url || '');
        }
        if (data.overlayBanner1920Url !== undefined) {
            setBanner1920Url(data.overlayBanner1920Url || '');
        }

        if (data.activeTheme) {
            setActiveTheme(data.activeTheme);
            if (data.activeTheme.qrCenterLogoUrl) {
                setQrCenterLogoUrl(data.activeTheme.qrCenterLogoUrl);
            }
        }
    }, [handleOverlayVisibilityChange]);

    // 7. Carga inicial de datos (REST) y conexión a SignalR
    useEffect(() => {
        let isMounted = true;

        const loadInitialData = async () => {
            try {
                // Cargar configuración pública
                const configRes = await GetPublicConfig(roomId || null);
                if (isMounted && configRes && configRes.success && configRes.data) {
                    applyOverlayData(configRes.data);
                    const isVisible = configRes.data.overlayVisible !== undefined
                        ? Boolean(configRes.data.overlayVisible)
                        : (configRes.data.isOverlayVisible !== undefined ? Boolean(configRes.data.isOverlayVisible) : false);
                    setFlowState(isVisible ? 'visible' : 'idle');
                }

                // Cargar leaderboard inicial
                const lbRes = await GetLeaderboard(roomId || null, 10);
                if (isMounted && lbRes && lbRes.success && Array.isArray(lbRes.data)) {
                    setLeaderboard(lbRes.data);
                }
            } catch (err) {
                console.warn('[PersonalSurferOverlay] Error en carga inicial:', err);
            }

            // Conectar a SignalR Hub
            try {
                await SignalRService.connect({ roomId, tipo: 'overlay' });
                if (isMounted) setConnectionStatus('connected');
            } catch (err) {
                console.error('[PersonalSurferOverlay] Error conectando a SignalR:', err);
                if (isMounted) setConnectionStatus('error');
            }
        };

        loadInitialData();

        // 8. Handlers de eventos SignalR
        const handleOverlayUpdated = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            applyOverlayData(data);
        };

        const handleConfigUpdated = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            applyOverlayData(data);
        };

        const handleThemeActivated = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (data && data.activeTheme) {
                setActiveTheme(data.activeTheme);
            }
        };

        const handleNewPlay = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (!data) return;

            // Refrescar Leaderboard cuando hay una nueva jugada
            GetLeaderboard(roomId || null, 10).then((res) => {
                if (isMounted && res && res.success && Array.isArray(res.data)) {
                    setLeaderboard(res.data);
                }
            }).catch(() => {});
        };

        const handleReset = () => {
            setLeaderboard([]);
            if (roomId) {
                GetLeaderboard(roomId, 10).then((res) => {
                    if (isMounted && res && res.success && Array.isArray(res.data)) {
                        setLeaderboard(res.data);
                    }
                }).catch(() => {});
            }
        };

        const handleReconnected = () => {
            if (isMounted) {
                setConnectionStatus('connected');
                loadInitialData();
            }
        };

        SignalRService.on('personalSurfer:overlayUpdated', handleOverlayUpdated);
        SignalRService.on('personalSurfer:configUpdated', handleConfigUpdated);
        SignalRService.on('personalSurfer:themeActivated', handleThemeActivated);
        SignalRService.on('personalSurfer:newPlay', handleNewPlay);
        SignalRService.on('personalSurfer:reset', handleReset);
        SignalRService.on('reconnected', handleReconnected);

        return () => {
            isMounted = false;
            SignalRService.off('personalSurfer:overlayUpdated', handleOverlayUpdated);
            SignalRService.off('personalSurfer:configUpdated', handleConfigUpdated);
            SignalRService.off('personalSurfer:themeActivated', handleThemeActivated);
            SignalRService.off('personalSurfer:newPlay', handleNewPlay);
            SignalRService.off('personalSurfer:reset', handleReset);
            SignalRService.off('reconnected', handleReconnected);
            SignalRService.disconnect();
        };
    }, [roomId, applyOverlayData]);

    return (
        <>
            <PersonalSurferView
                flowState={flowState}
                overlayMode={overlayMode}
                qrCount={qrCount}
                offsetY={offsetY}
                banner1536Url={banner1536Url}
                banner1920Url={banner1920Url}
                qrCenterLogoUrl={qrCenterLogoUrl}
                leaderboard={leaderboard}
                activeTheme={activeTheme}
                roomId={roomId}
            />

            {connectionStatus === 'error' && (
                <div style={{
                    position: 'fixed',
                    bottom: 5,
                    right: 5,
                    background: 'red',
                    color: 'white',
                    padding: '3px 8px',
                    fontSize: '9px',
                    borderRadius: '4px',
                    opacity: 0.8,
                    zIndex: 99999
                }}>
                    D
                </div>
            )}
        </>
    );
}

PersonalSurferOverlay.propTypes = {
    roomId: PropTypes.string
};

export default PersonalSurferOverlay;
